import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { concat, Subscription, of, from, Observable } from 'rxjs';
import { map, concatMap, switchMap, finalize } from 'rxjs/operators';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { SalesLead, SalesLeadItem } from 'src/app/shared/sales-leads'

import { ClientApiService } from 'src/app/services/client-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { SalesApiService } from 'src/app/services/sales-api.service';

@Component({
  selector: 'app-edit-sales-lead',
  templateUrl: './edit-sales-lead.component.html',
  styleUrls: ['./edit-sales-lead.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Sales lead (deal / project) editor.
 *
 * Loading uses a simple `getSalesLeadById` call (no `concatMap` chain) with a `finalize`
 * snapshot to `salesLeadCopy`. New leads (salesLeadId == 0) are created in-memory with
 * `DOCREGID: 0`.
 *
 * `onConfirm` PUTs existing leads (auto-stamping `DOCREGUDATE = new Date()`) or POSTs new
 * ones and uses `finalize` to route to the new ID.
 *
 * `noChangesThere` skips `DOCREGCDATE` and `DOCREGUDATE` when comparing header fields
 * because those timestamps are set server-side and change on every save.
 *
 * Subscription management is manual (old-style named fields).
 */
export class EditSalesLeadComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {

  globalSettinsData: GlobalSettingsData;

  /*private*/ salesLeadId: number = 0;


  mainCompanyId: number = 0;
  responcePersonId: number = 0;

  SLCREATEDATE: Date;
  SLCHANGEPDATE: Date;

  private _initialQuery: Subscription;
  private _productInfoQuery: Subscription;
  private _deleteDocQuery: Subscription;
  private _childDocQuery: Subscription;
  private _documentQuery: Subscription;
  private _docNameQuery: Subscription;
  private _documentSaveQuery: Subscription;
  private _documentPostQuery: Subscription;

  salesLead: SalesLead;
  salesLeadCopy: SalesLead;

  displayDC: boolean;
  displayND: boolean;
  displayMD: boolean;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;
  confirmExportDocumentText: string;

  docHeaderStatement: string = '';
  docFooterStatement: string = '';

  toolbarOptions: any = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  modules: any = {
    toolbar: this.toolbarOptions
  };

  /** `true` when no sales-lead property has changed; skips `DOCREGCDATE` and `DOCREGUDATE`. */
  public get noChangesThere(): boolean {
    let equal: boolean = (this.salesLead && this.salesLeadCopy) ? true : false;
    if (equal) {
      for (var props in this.salesLead) {

        switch (props) {
          case 'DOCREGCDATE':
          case 'DOCREGUDATE':
            break;

          default:
            equal = (this.salesLead[props] == this.salesLeadCopy[props]);
            break;
        }
        if (!equal) {
          break;
        }
      }
    }
    return equal;
  }

  /** `true` when the sales lead object is not yet loaded. */
  public get formInvalid(): boolean {
    return (
      !this.salesLead
    );
  }

  /** `true` when the lead is loaded, saved, and has an existing ID — enables child-doc creation. */
  public get canCreate(): boolean { return (this.salesLeadId) && (this.salesLead) && (this.noChangesThere); }

  /** `true` when the lead is loaded and has an existing ID — enables delete. */
  public get canDelete(): boolean { return (this.salesLeadId) && (this.salesLead && true); }

  editing: boolean = false;

  constructor(
    private docApi: DocumentApiService,
    private router: Router,
    private route: ActivatedRoute,
    private salesApi: SalesApiService,
    private cliApi: ClientApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) { }

  /** Guards navigation; blocks when there are unsaved changes or an active inline edit. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return (this.noChangesThere && !this.editing);
  }

  /** Unsubscribes all active queries. */
  ngOnDestroy(): void {
    console.log('*** EditSalesLeadComponent in ngOnDestroy')

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._documentQuery) { this._documentQuery.unsubscribe(); }
    if (this._documentSaveQuery) { this._documentSaveQuery.unsubscribe(); }
    if (this._documentPostQuery) { this._documentPostQuery.unsubscribe(); }

    if (this._docNameQuery) { this._docNameQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._deleteDocQuery) { this._deleteDocQuery.unsubscribe(); }
    if (this._childDocQuery) { this._childDocQuery.unsubscribe(); }
  }

  /** Logs Angular change-detection cycles (diagnostic only). */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** EditSalesLeadComponent ngOnChanges: ===> ' + JSON.stringify(changes));
  }

  /**
   * Loads the sales lead via `getSalesLeadById` with a `finalize` snapshot to `salesLeadCopy`.
   * For new leads (salesLeadId == 0), creates a blank in-memory object.
   */
  loadDocumentContent() {
    console.log('*** EditSalesLeadComponent loadSalesLeadContent: ===> Started');
    if (this.salesLeadId) {
      console.log('*** EditSalesLeadComponent salesLeadId: ' + this.salesLeadId);

      this._documentQuery =
        this.salesApi.getSalesLeadById(this.salesLeadId)
        .pipe(
          map(data => {
            this.salesLead = data[0];
            this.SLCREATEDATE = new Date(this.salesLead.DOCREGCDATE);
            this.SLCHANGEPDATE = new Date(this.salesLead.DOCREGUDATE);
            this.responcePersonId = this.salesLead.DOCREGRESPERSON;
          }),
          finalize(() => {
            this.salesLeadCopy = { ... this.salesLead };

            this.titleService.setTitle('Сделка (Проект) ' + this.salesLead.DOCREGID);
          }))
        .subscribe();
    } else {
      this.salesLead = {
        DOCREGID: 0,
        DOCREGCDATE: new Date(),
        DOCREGUDATE: new Date(),
        DOCREGNAME: 'New Sales Lead',
        DOCREGNOTE: '',
        DOCREGDESCRIPTION: '',
        DOCREGRESPERSON: this.responcePersonId,
      };
      this.SLCREATEDATE = new Date(this.salesLead.DOCREGCDATE);
      this.SLCHANGEPDATE = new Date(this.salesLead.DOCREGUDATE);
      this.titleService.setTitle('Новая Сделка (Проект) - создание');
    }
    console.log('*** EditSalesLeadComponent loadDocumentContent: ===> Ended');
  }

  /** Reads global settings and subscribes to route params to trigger `loadDocumentContent`. */
  ngOnInit(): void {
    console.log('*** EditSalesLeadComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.mainCompanyId = this.globalSettinsData.mainCompanyId;
    this.responcePersonId = this.globalSettinsData.mainPersonId;

    this._initialQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this.salesLeadId = +params.get('id');
          console.log('*** EditSalesLeadComponent in param subscribe: (' + this.salesLeadId + ') ' + JSON.stringify(params));

          this.loadDocumentContent();
        });
  }

  /** Updates `DOCREGCDATE` and the local `SLCREATEDATE` date model from the date picker. */
  onDateSelect(event) {
    console.log('*** EditSalesLeadComponent in onDateSelect: ' + JSON.stringify(event));
    let dddd = new Date(event);

    this.SLCREATEDATE = dddd;
    this.salesLead.DOCREGCDATE = dddd;
  }

  /**
   * Saves the sales lead. PUT existing leads (auto-stamping `DOCREGUDATE`) or POST new ones
   * using `finalize` to route to the returned ID.
   */
  onConfirm(e: number) {
    console.log('*** EditSalesLeadComponent onConfirm: ' + e)

    if (this.salesLeadId) {
      this.salesLead.DOCREGUDATE = new Date();

      this._documentSaveQuery =
        this.salesApi.putSalesLeadById(this.salesLead)
          .pipe(map(data => {
            this.salesLeadCopy = { ... this.salesLead };
            this.salesLead = { ... this.salesLead };        // Trigger the changes in child components
            console.log('*** EditSalesLeadComponent onConfirm: *** PUT *** ');
          }))
          .subscribe();
    } else {
      this._documentPostQuery =
        this.salesApi.postSalesLeadById(this.salesLead)
        .pipe(
          map(data => {
            console.log('*** EditSalesLeadComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
            // postSalesLeadById returns { DOCREGID }, not a full SalesLead (Prompt 13-G
            // create-ID shape) -- set the server-assigned ID on the existing entity rather
            // than overwriting it, then re-spread to trigger child component refresh.
            this.salesLead.DOCREGID = data['DOCREGID'];
            this.salesLeadCopy = { ... this.salesLead };
            this.salesLead = { ... this.salesLead };
          }),
          finalize(() => {
            this.router.navigate(['/edit-sales-lead', this.salesLead.DOCREGID]);
            console.log('*** EditSalesLeadComponent onConfirm: *** POST *** ');
          })
        )
        .subscribe();
    }
  }

  /** Re-spreads `salesLead` to trigger child component refresh after a refill operation. */
  onRefillDocument(event) {
    console.log('*** EditSalesLeadComponent onRefillDocument: ' + JSON.stringify(event))
    if (event) {
      this.salesLead = { ...this.salesLead }; // Trigger reload
    }
  }
    
  /** Deletes the sales lead and navigates back to `/sales-leads` after confirmation. */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditSalesLeadComponent onDeleteDocumentConfirmation: '/* + JSON.stringify(event)*/);

    this._deleteDocQuery =
      this.salesApi.deleteSalesLeadById(this.salesLead).subscribe(() => {
        console.log('*** EditSalesLeadComponent onDeleteDocumentConfirmation: DONE!');
        this.router.navigate(['/sales-leads']);
      });
  }

  /** Opens the delete confirmation dialog for this sales lead. */
  deleteDocumentClick(event) {
    console.log('*** EditSalesLeadComponent deleteDocumentClick: ' /*+ JSON.stringify(event)*/);

    this.confirmDeleteDocumentText = 'DELETE ' + this.salesLead.DOCREGID;
    this.confirmDeleteDocumentOn = true;
  }

  /** Applies the selected responsible person ID to `salesLead.DOCREGRESPERSON`. */
  cliPersonSelected(selectedId) {
    /*
     * TODO: Select the EditDocumentClientComponent person in PCO group (have right to sign the doc)
     */
    console.log('*** EditSalesLeadComponent in cliPersonSelected: ' + selectedId);
    this.salesLead.DOCREGRESPERSON = selectedId;
  }


}
